import React, { useEffect, useRef, useState } from 'react';
import { ClickAwayListener } from '@mui/material';
import cx from 'classnames';
import { timeLineFormatTime, parseColorToHex } from '../../../helpers';
import { clipLabel, CommentClip } from './clipComments';
import styles from './VideoComments.module.scss';

export const CommentTargetCard = ({ video, candidates, clip, selected, onPick }: {
  video: boolean; candidates: CommentClip[]; clip?: CommentClip; selected: boolean; onPick: (clip: CommentClip) => void;
}) => {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const picker = useRef<HTMLDivElement>(null);
  const candidateKey = candidates.map((candidate) => candidate.id).join(',');
  const ambiguous = !video && candidates.length > 1;
  useEffect(() => { setOpen(false); }, [candidateKey, video]);
  useEffect(() => {
    if (open) picker.current?.querySelector<HTMLButtonElement>('[aria-pressed="true"], button')?.focus();
  }, [open]);
  const label = video ? 'Whole video' : clip ? clipLabel(clip) : ambiguous ? 'Choose a clip' : 'No clip at playhead';
  const badge = video ? 'Video' : ambiguous ? clip ? `1 of ${candidates.length} here ▾` : `${candidates.length} overlap here ▾`
    : clip ? selected ? 'Selected clip' : 'At playhead' : 'Disabled';
  const content = <>
    <span className={styles.targetSwatch} style={{ background: clip ? clip.color?.startsWith('#') ? clip.color : parseColorToHex(clip.color) : ambiguous ? '#ffb347' : '#5c6597' }} />
    <span className={styles.targetText}><span className={styles.targetCaption}>Commenting on</span><strong>{label}</strong></span>
    <span className={styles.targetBadge}>{badge}</span>
  </>;
  return <ClickAwayListener onClickAway={() => setOpen(false)}><div className={styles.targetWrapper} aria-live="polite" onKeyDown={(event) => {
    event.stopPropagation();
    if (event.key === 'Escape' && open) { setOpen(false); trigger.current?.focus(); }
  }}>
    {ambiguous ? <button ref={trigger} type="button" id="sidebar-comment-target" className={cx(styles.commentTarget, !clip && styles.chooseTarget)}
      aria-expanded={open} aria-controls={open ? 'sidebar-clip-candidates' : undefined} onClick={() => setOpen((value) => !value)}>{content}</button>
      : <div id="sidebar-comment-target" className={cx(styles.commentTarget, !video && !clip && styles.disabledTarget)} role="status">{content}</div>}
    {open && ambiguous && <div ref={picker} id="sidebar-clip-candidates" className={styles.targetPicker} role="group" aria-label="Choose a clip for your comment">
      <p>{candidates.length} clips overlap here. Which is this comment for?</p>
      {candidates.map((candidate) => <button key={candidate.id} type="button" aria-pressed={candidate.id === clip?.id} onClick={() => {
        onPick(candidate); setOpen(false); trigger.current?.focus();
      }}><span>{clipLabel(candidate)}</span><small>{timeLineFormatTime(candidate.time_start).trim()}–{timeLineFormatTime(candidate.time_end).trim()}</small></button>)}
    </div>}
  </div></ClickAwayListener>;
};
